import React, { ChangeEvent, ComponentProps, forwardRef, memo } from 'react'

import styled from '@emotion/styled'

import { isFunction } from '@surglogs/util.is-function'

import { useHandler } from '@surglogs-app/hooks'

import { InputStyleProps, inputStyles } from './Input'

const getTextAreaHeight = (e: ChangeEvent<HTMLTextAreaElement>) => {
  return e?.target?.scrollHeight
}

const StyledTextarea = styled.textarea<InputStyleProps>`
  // 3 rows + padding (3 x 22 + 26)
  min-height: 92px;

  // to keep same padding as in input - (height (48px) - line-height (22px) / 2) = 13px
  padding-block: ${({ theme }) =>
    `calc((${theme.ui.spacings.xxl} - ${theme.ui.typography.lineHeights.sm}) / 2)`};
  padding-inline: ${({ theme }) => theme.ui.spacings.md};

  ${inputStyles};
`

interface TextInputProps extends ComponentProps<typeof StyledTextarea> {
  invalid?: boolean
  onHeightChange?: (height: number) => void
}

export const TextInput = memo(
  forwardRef<HTMLTextAreaElement, TextInputProps>(
    ({ invalid, onHeightChange, placeholder, ...rest }, ref) => {
      const onChange = useHandler((e) => {
        if (isFunction(rest.onChange)) {
          rest.onChange(e)
        }

        if (isFunction(onHeightChange)) {
          const height = getTextAreaHeight(e)
          onHeightChange(height)
        }
      })

      const onClick = useHandler((e) => {
        if (isFunction(onHeightChange)) {
          const height = getTextAreaHeight(e)
          onHeightChange(height)
        }

        if (isFunction(rest.onClick)) {
          rest.onClick(e)
        }
      })

      return (
        <StyledTextarea
          {...rest}
          {...(isFunction(onHeightChange)
            ? {
                onChange,
                onClick,
              }
            : {})}
          {...{
            ref,
            placeholder,
            'aria-invalid': invalid,
            invalid,
          }}
        />
      )
    },
  ),
)
